import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getSocket, type BookingChangeEvent } from '@/lib/realtime';
import { useAuth } from '@/lib/auth';

/**
 * Suscribe a cambios de un booking específico vía WebSocket.
 * Invalida queries de TanStack Query cuando llega un evento booking-change.
 */
export function useBookingRealtime(bookingId: number | null | undefined) {
  const qc = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (bookingId == null) return;
    const socket = getSocket();

    function onChange(ev: BookingChangeEvent) {
      if (ev.bookingId !== bookingId) return;
      qc.invalidateQueries({ queryKey: ['booking-items', ev.bookingId] });
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['my-work'] });
      qc.invalidateQueries({ queryKey: ['my-work-count'] });
      qc.invalidateQueries({ queryKey: ['team-agentes'] });
      qc.invalidateQueries({ queryKey: ['item', ev.itemId] });
      qc.invalidateQueries({ queryKey: ['item-history', ev.itemId] });

      // Toast cuando el booking se completa — solo a OTROS usuarios (el que
      // hizo el cambio ya vio el toast en EstadoSelector / ItemDetailDialog).
      if (ev.bookingJustCompleted && user && ev.changedBy.id !== user.id) {
        toast.success(`🎉 Booking ${ev.bookingIdExterno ?? ''} completado`, {
          description: `Cerrado por ${ev.changedBy.nombre}`,
          duration: 6000,
        });
      }
    }

    function subscribe() {
      socket.emit('subscribe-booking', { bookingId });
    }

    socket.on('connect', subscribe);
    socket.on('booking-change', onChange);
    if (socket.connected) subscribe();

    return () => {
      socket.emit('unsubscribe-booking', { bookingId });
      socket.off('connect', subscribe);
      socket.off('booking-change', onChange);
    };
  }, [bookingId, qc]);
}
